import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OrganizationApiService } from '@core/api/organization-api.service';
import { User } from '@core/models/user.model';
import { Workspace } from '@core/models/workspace.model';
import { isPendingMember } from '@core/utils/invitation-status.util';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { WorkspaceService } from './workspace.service';

/** Sentinel for "invitations could not be read" — never treated as "none". */
const UNKNOWN = -1;

/**
 * The one place that decides where a signed-in user belongs.
 *
 * The decision needs two facts, and `myOrganizations` only carries one of them: it
 * lists accepted memberships, so a user holding nothing but a pending invitation
 * looks membership-less. Routing on that count alone strands them on
 * /workspace-creation with the invitation hidden.
 *
 * So the invitation count is fetched *before* navigating rather than after landing
 * somewhere: callers go straight to the final destination, with no visible hop
 * through the workspace hub on the way to workspace creation.
 */
@Injectable({ providedIn: 'root' })
export class LandingRouteService {
  private auth = inject(AuthService);
  private ws = inject(WorkspaceService);
  private router = inject(Router);
  private orgApi = inject(OrganizationApiService);

  /** Resolve the final landing route for a freshly signed-in user. */
  async resolve(user: User): Promise<string> {
    if (user.platformRole === 'super_admin' || this.auth.isSuperAdmin()) {
      return '/platform/dashboard';
    }

    let memberships = user.workspaces;
    let invitations = await this.pendingInvitationCount();

    // Nothing at all is also what a membership that has not replicated through the
    // message bus yet looks like (join links, invite-token signups). One re-read
    // before committing someone to workspace creation.
    if (memberships.length === 0 && invitations === 0) {
      const refreshed = await firstValueFrom(this.auth.refreshSession()).catch(
        () => null,
      );
      if (refreshed) {
        memberships = refreshed.workspaces;
        invitations = await this.pendingInvitationCount();
      }
    }

    return this.routeFor(memberships, invitations);
  }

  /** Resolve and navigate in one step. */
  async navigate(user: User): Promise<boolean> {
    return this.router.navigateByUrl(await this.resolve(user));
  }

  /**
   * Where a user with these memberships and this many actionable invitations
   * belongs. Auto-entering selects the workspace as a side effect.
   */
  routeFor(memberships: Workspace[], invitations: number): string {
    // Unreadable invitations: the hub shows the failure and can retry, whereas
    // workspace creation would quietly bury an invitation that does exist.
    if (invitations === UNKNOWN) {
      return memberships.length === 0
        ? '/select-workspace'
        : this.enter(memberships);
    }

    if (memberships.length === 0) {
      return invitations > 0 ? '/select-workspace' : '/workspace-creation';
    }

    // Skip the picker only when there is nothing to decide about.
    if (memberships.length === 1 && invitations === 0) {
      return this.enter(memberships);
    }

    return '/select-workspace';
  }

  /** Actionable invitations, or `UNKNOWN` when the call failed. */
  async pendingInvitationCount(): Promise<number> {
    try {
      const list = await firstValueFrom(this.orgApi.getMyInvitations());
      return list.filter(
        (inv) => !inv.invitationStatus || isPendingMember(inv.invitationStatus),
      ).length;
    } catch {
      return UNKNOWN;
    }
  }

  private enter(memberships: Workspace[]): string {
    if (memberships.length === 1) this.ws.select(memberships[0]);
    return this.ws.homeRoute();
  }
}

export { UNKNOWN as INVITATIONS_UNKNOWN };
